import React from 'react';
import {
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import EditNoteIcon from '@mui/icons-material/EditNote';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import UndoIcon from '@mui/icons-material/Undo';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { VARIANCE_ACTIONS, VARIANCE_ACTION_LABELS, varianceActionsForSource } from './varianceActions.js';
import { inheritedCpqOverrideFromContext } from './varianceActionEvidenceSnapshot.js';

const actionIcon = (actionType) => {
  if (actionType === VARIANCE_ACTIONS.EDIT_CHANGE) return <EditNoteIcon fontSize="small" />;
  if (actionType === VARIANCE_ACTIONS.UNDO_CHANGE || actionType === VARIANCE_ACTIONS.CLEAR_INHERITED_CPQ) return <UndoIcon fontSize="small" />;
  if (actionType === VARIANCE_ACTIONS.VIEW_DETAILS) return <InfoOutlinedIcon fontSize="small" />;
  if (actionType === VARIANCE_ACTIONS.CUSTOM) return <EditNoteIcon fontSize="small" />;
  if (actionType === VARIANCE_ACTIONS.CHANGE_PRICE || actionType === VARIANCE_ACTIONS.DPE_CHANGE_PRICE_ACTION) return <AttachMoneyIcon fontSize="small" />;
  if (actionType === VARIANCE_ACTIONS.REPLACE_PRODUCT) return <SwapHorizIcon fontSize="small" />;
  if (actionType.startsWith('remove') || actionType === VARIANCE_ACTIONS.DPE_CANCEL_ACTION) return <RemoveCircleOutlineIcon fontSize="small" />;
  return <AddCircleOutlineIcon fontSize="small" />;
};

const needsDividerBefore = (actionType) => (
  actionType === VARIANCE_ACTIONS.REPLACE_PRODUCT
  || actionType === VARIANCE_ACTIONS.CHANGE_PRICE
  || actionType === VARIANCE_ACTIONS.DPE_CHANGE_PRICE_ACTION
);

const EDITABLE_DPE_STATUSES = new Set(['draft', 'saved', 'error']);
const canEditDraftAction = (action = {}) => {
  if (!action?.id) return false;
  if (!Object.prototype.hasOwnProperty.call(action, 'queueStatus')) return true;
  return EDITABLE_DPE_STATUSES.has(String(action.queueStatus || '').trim().toLowerCase());
};

const VarianceActionMenu = ({ state, onClose, onSelect, allowDpeActions = true }) => {
  const context = state?.context;
  const draftAction = context?.draftPreview?.action;
  const inheritedCpqOverride = inheritedCpqOverrideFromContext(context);
  const clearInheritedCpqActions = inheritedCpqOverride.hasOverride
    ? [VARIANCE_ACTIONS.CLEAR_INHERITED_CPQ]
    : [];
  const actions = draftAction?.id
    ? [
      VARIANCE_ACTIONS.VIEW_DETAILS,
      ...(canEditDraftAction(draftAction) ? [VARIANCE_ACTIONS.EDIT_CHANGE, VARIANCE_ACTIONS.UNDO_CHANGE] : []),
      ...clearInheritedCpqActions,
      ...varianceActionsForSource(context?.source, { allowDpeActions }),
    ]
    : [
      VARIANCE_ACTIONS.VIEW_DETAILS,
      ...clearInheritedCpqActions,
      ...varianceActionsForSource(context?.source, { allowDpeActions }),
    ];
  const anchorPosition = state ? { top: state.mouseY, left: state.mouseX } : undefined;

  return (
    <Menu
      open={Boolean(state)}
      onClose={onClose}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition}
      slotProps={{
        paper: {
          sx: { minWidth: 230, borderRadius: 1.5 },
        },
      }}
    >
      {actions.flatMap((actionType, index) => {
        const item = (
          <MenuItem key={actionType} onClick={() => onSelect(actionType, context)}>
            <ListItemIcon>{actionIcon(actionType)}</ListItemIcon>
            <ListItemText
              primary={VARIANCE_ACTION_LABELS[actionType]}
              primaryTypographyProps={{ variant: 'body2', fontWeight: 700 }}
            />
          </MenuItem>
        );
        if (
          index > 0
          && (
            needsDividerBefore(actionType)
            || actionType === VARIANCE_ACTIONS.CLEAR_INHERITED_CPQ
            || actionType === VARIANCE_ACTIONS.CUSTOM
          )
        ) {
          return [<Divider key={`${actionType}-divider`} />, item];
        }
        return [item];
      })}
    </Menu>
  );
};

export default VarianceActionMenu;
